import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { DocumentToTemplateFromUrlBinder } from 'shared/domain/document/documentBinder';
import { useProjectId } from 'store/project';
import { ErrorPresentation } from 'helpers/validators';
import { useRedirect } from 'hooks/useRedirect';
import { FormattedMessage } from 'intl';
import {
  DialogTranslationKeys,
  InspectionTemplateTranslationKeys,
} from 'intl/keys';
import { translationKeyToString } from 'intl/types/translationKey';

import { useLocation } from 'react-router-dom';
import {
  showErrorToastAndLog,
  showErrorToastWithMessage,
} from 'services/toaster';
import { ProcessType } from 'shared/domain/process/types/model';
import { Process } from 'shared/domain/process/types/model';
import { useProcesses } from 'store/process';
import useFormEdit from 'views/issue/forms/useFormEdit';
import { logError } from 'setup/logger/logError';
import { useInspectionTemplate } from 'store/inspection';
import {
  generateOrder,
} from 'shared/domain/template/templateWizardDraft';
import { TemplateWizardSubmitResult } from 'shared/domain/template/templateWizardFlow';
import { TemplateDefaultValues } from '../populator/types';
import {
  canPublishInspectionTemplate,
  copyChecklistItemValues,
  copySectionValues,
} from './model';
import {
  buildAllSiblings,
  calculateNewOrder,
  chooseTitle,
  createChecklistItem,
  createSection,
  createGUSettings,
  findFormEntityIndex,
  matchesFormEntityId,
  isEmptyChangeInObject,
  scrollToFirstError,
} from './helpers';
import { MemoPresentational } from './presentational';
import {
  CreateInspectionTemplateErrors,
  FormChecklistAnswer,
  FormChecklistItem,
  FormSection,
  ChecklistItemValidationFocus,
  TemplateValidItem,
  TemplateWizardFormProps,
} from './types';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { createFormChecklistAnswer } from './answerHelpers';
import {
  chooseOptimalChecklistErrors,
  chooseOptimalErrors,
  chooseOptimalSectionErrors,
  checklistAnswerErrorsByAnswerId,
  descriptionValidation,
  findFirstChecklistValidationFocus,
  hasAnswersTabErrors,
  processValidation,
  summaryValidation,
  titleValidation,
  validateFormChecklistAnswers,
} from './validation';
import { GU as GraphicUploaderType } from 'components/common/graphicUploader/types';
import { CommonTranslationKeys } from 'intl/keys';
import {
  GeneralNormConfig,
} from 'shared/domain/template/generalNormConfig';

function TemplateWizardForm(props: TemplateWizardFormProps): ReactElement {
  const projectId = useProjectId();
  const location = useLocation();
  const pageTitleId = chooseTitle(location.pathname).id;
  const formName = 'template';
  const {
    originalDefaultValues,
    defaultValues,
    submitHandler,
    publishHandler,
    generalNormDocuments,
    idParam,
  } = props;
  const refDefaultValues = useRef<TemplateDefaultValues>(defaultValues);
  const createDialog = useDialog();
  const redirect = useRedirect();
  const { setEditing } = useFormEdit();
  /** Aktywne ID szablonu w slice `inspectionTemplate` (to nie jest `inspectionId` od inspekcji/badania). */
  const { openInspectionTemplate } = useInspectionTemplate();

  const { processes: allProcesses } = useProcesses();
  const processes = useMemo(
    () =>
      allProcesses.filter(
        (process: ProcessType) => process._id !== Process.TSK
      ),
    [allProcesses]
  );

  const [isSaveDraftInProgress, setSaveDraftInProgress] =
    React.useState<boolean>(false);
  const [isPublishing, setPublishing] = React.useState<boolean>(false);
  const [checklistValidationFocus, setChecklistValidationFocus] =
    React.useState<ChecklistItemValidationFocus | null>(null);

  const [summary, setSummary] = React.useState<string>(
    defaultValues.summary
  );
  const [process, setProcess] = React.useState<string | null>(
    defaultValues.process
  );
  const [generalNorm, setGeneralNorm] = React.useState<string>(
    defaultValues.generalNorm.description
  );
  const [generalNormConfig, setGeneralNormConfig] =
    React.useState<GeneralNormConfig>(() =>
      defaultValues.generalNormConfig
    );

  const [sections, setSections] = React.useState<FormSection[]>(
    copySectionValues(defaultValues.sections)
  );

  const [items, setItems] = React.useState<FormChecklistItem[]>(
    copyChecklistItemValues(defaultValues.checklist)
  );

  const [errors, setErrors] =
    React.useState<CreateInspectionTemplateErrors>({
      summary: undefined,
      process: undefined,
      description: undefined,
    });

  const [saveDraft, setSaveDraft] = React.useState<boolean>(false);
  const [saveAndPublish, setSaveAndPublish] =
    React.useState<boolean>(false);

  const GU = useGraphicUploader(generalNormDocuments, {
    ...createGUSettings(idParam),
    documentBinderFactory: (baseUrl?: string) => {
      if (!baseUrl) throw new Error('From url binder requires URL');
      return new DocumentToTemplateFromUrlBinder(baseUrl);
    },
  });

  const handleTemplateSummaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setSummary(newValue);
      setEditing(true, formName);
      setErrors((prev) => {
        const validation = summaryValidation(newValue);
        if (isEmptyChangeInObject(prev, validation, 'summary')) {
          return prev;
        }
        return {
          ...prev,
          summary: validation,
        };
      });
    },
    [setEditing]
  );

  const handleProcessChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newProcess: string | null) => {
      if (newProcess !== null) {
        setEditing(true, formName);
        setProcess(newProcess);
        setErrors((prev) => {
          if (isEmptyChangeInObject(prev, undefined, 'process')) {
            return prev;
          }
          return {
            ...prev,
            process: undefined,
          };
        });
      }
    },
    [setEditing]
  );

  const handleGeneralNormDescriptionChange = useCallback(
    (html: string) => {
      setEditing(true, formName);
      setGeneralNorm(html);
      setErrors((prev) => {
        const validation = descriptionValidation(html);
        if (isEmptyChangeInObject(prev, validation, 'description')) {
          return prev;
        }
        return {
          ...prev,
          description: validation,
        };
      });
    },
    [setEditing]
  );

  const handleGeneralNormConfigChange = useCallback(
    <K extends keyof GeneralNormConfig>(field: K, value: GeneralNormConfig[K]) => {
      setEditing(true, formName);
      setGeneralNormConfig((previous) => ({ ...previous, [field]: value }));
    },
    [setEditing]
  );

  const handleAddSection = useCallback(
    (parentSectionId?: string | null) => {
      setEditing(true, formName);
      setSections((prev) => {
        const parentId = parentSectionId ?? null;
        const sectionsInParent = prev.filter(
          (section) => section.parentSectionId === parentId
        );
        const itemsInParent = items.filter(
          (item) => item.parentSectionId === parentId
        );
        const sectionToAdd = createSection(parentId, prev);

        return [
          ...prev,
          {
            ...sectionToAdd,
            order: generateOrder([...sectionsInParent, ...itemsInParent]),
          },
        ];
      });
    },
    [items, setEditing]
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      setEditing(true, formName);
      setSections((prev) => {
        const sectionsToRemove = new Set<string>();
        sectionsToRemove.add(sectionId);
        let foundNew = true;
        while (foundNew) {
          foundNew = false;
          prev.forEach((section) => {
            const sectionKey = section._id || section.uniqueKeyId;
            if (
              section.parentSectionId &&
              sectionsToRemove.has(section.parentSectionId) &&
              !sectionsToRemove.has(sectionKey)
            ) {
              sectionsToRemove.add(sectionKey);
              foundNew = true;
            }
          });
        }
        return prev.filter(
          (section) =>
            !sectionsToRemove.has(section._id || section.uniqueKeyId)
        );
      });
      setItems((prev) =>
        prev.filter(
          (item) =>
            item.parentSectionId !== sectionId &&
            !sections.some(
              (s) =>
                s.parentSectionId === sectionId &&
                matchesFormEntityId(s, item.parentSectionId || '')
            )
        )
      );
    },
    [setEditing, sections]
  );

  const handleSectionTitleChange = useCallback(
    (sectionId: string, title: string) => {
      setEditing(true, formName);
      setSections((prev) => {
        const index = findFormEntityIndex(prev, sectionId);
        if (index === -1) return prev;
        const validation = titleValidation(title);
        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            title,
            errors: {
              ...prev[index].errors,
              title: validation,
            },
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    [setEditing]
  );

  const handleSectionDescriptionChange = useCallback(
    (sectionId: string, description: string) => {
      setEditing(true, formName);
      setSections((prev) => {
        const index = findFormEntityIndex(prev, sectionId);
        if (index === -1) return prev;
        const validation = descriptionValidation(description);
        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            description,
            errors: {
              ...prev[index].errors,
              description: validation,
            },
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    [setEditing]
  );

  const handleSectionGraphicUploaderInit = useCallback(
    (sectionId: string, uploader: GraphicUploaderType) => {
      setSections((prev) => {
        const index = findFormEntityIndex(prev, sectionId);
        if (index === -1) return prev;
        if (prev[index].graphicUploader === uploader) return prev;

        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            graphicUploader: uploader,
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    []
  );

  const handleAddItem = useCallback(
    (parentSectionId?: string | null) => {
      setEditing(true, formName);
      setItems((prev) => [...prev, createChecklistItem(parentSectionId ?? null, prev)]);
    },
    [setEditing]
  );

  const handleDeleteItem = useCallback(
    (itemId: string) => {
      setEditing(true, formName);
      setItems((prev) =>
        prev.filter((item) => !matchesFormEntityId(item, itemId))
      );
    },
    [setEditing]
  );

  const handleItemSummaryChange = useCallback(
    (itemId: string, value: string) => {
      setEditing(true, formName);
      setItems((prev) => {
        const index = findFormEntityIndex(prev, itemId);
        if (index === -1) return prev;
        const validation = summaryValidation(value);
        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            summary: value,
            errors: {
              ...prev[index].errors,
              summary: validation,
            },
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    [setEditing]
  );

  const handleItemDescriptionChange = useCallback(
    (itemId: string, value: string) => {
      setEditing(true, formName);
      setItems((prev) => {
        const index = findFormEntityIndex(prev, itemId);
        if (index === -1) return prev;
        const validation = descriptionValidation(value);
        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            description: value,
            errors: {
              ...prev[index].errors,
              description: validation,
            },
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    [setEditing]
  );

  const handleItemGraphicUploaderInit = useCallback(
    (itemId: string, uploader: GraphicUploaderType) => {
      setItems((prev) => {
        const index = findFormEntityIndex(prev, itemId);
        if (index === -1) return prev;
        if (prev[index].graphicUploader === uploader) return prev;

        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            graphicUploader: uploader,
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    []
  );

  const handleReorderSection = useCallback(
    (
      sectionId: string,
      beforeId?: string,
      afterId?: string,
      newParentSectionId?: string | null
    ) => {
      setEditing(true, formName);
      setSections((prev) => {
        const sectionIndex = findFormEntityIndex(prev, sectionId);
        if (sectionIndex === -1) return prev;

        const section = prev[sectionIndex];
        const parentId =
          newParentSectionId !== undefined
            ? newParentSectionId
            : section.parentSectionId;

        const siblings = buildAllSiblings(prev, items, parentId, sectionId);
        const newOrder = calculateNewOrder(siblings, beforeId, afterId);

        return [
          ...prev.slice(0, sectionIndex),
          { ...section, parentSectionId: parentId, order: newOrder },
          ...prev.slice(sectionIndex + 1),
        ];
      });
    },
    [setEditing, items]
  );

  const handleReorderItem = useCallback(
    (
      itemId: string,
      beforeId?: string,
      afterId?: string,
      newParentSectionId?: string | null
    ) => {
      setEditing(true, formName);
      setItems((prev) => {
        const itemIndex = findFormEntityIndex(prev, itemId);
        if (itemIndex === -1) return prev;

        const item = prev[itemIndex];
        const parentId =
          newParentSectionId !== undefined
            ? newParentSectionId
            : item.parentSectionId;

        const siblings = buildAllSiblings(sections, prev, parentId, itemId);
        const newOrder = calculateNewOrder(siblings, beforeId, afterId);

        return [
          ...prev.slice(0, itemIndex),
          { ...item, parentSectionId: parentId, order: newOrder },
          ...prev.slice(itemIndex + 1),
        ];
      });
    },
    [setEditing, sections]
  );

  const handleAddAnswer = useCallback(
    (itemId: string, answerType: AnswerType) => {
      setEditing(true, formName);
      setItems((prev) => {
        const index = findFormEntityIndex(prev, itemId);
        if (index === -1) return prev;

        const newAnswer = createFormChecklistAnswer(answerType);
        return [
          ...prev.slice(0, index),
          {
            ...prev[index],
            answers: [...(prev[index].answers || []), newAnswer],
          },
          ...prev.slice(index + 1),
        ];
      });
    },
    [setEditing]
  );

  const handleUpdateAnswer = useCallback(
    (itemId: string, answerId: string, updates: Partial<FormChecklistAnswer>) => {
      setEditing(true, formName);
      setItems((prev) => {
        const itemIndex = findFormEntityIndex(prev, itemId);
        if (itemIndex === -1) return prev;

        const item = prev[itemIndex];
        const answerIndex = findFormEntityIndex(item.answers || [], answerId);
        if (answerIndex === -1) return prev;

        const updatedAnswers = [...item.answers];
        updatedAnswers[answerIndex] = {
          ...updatedAnswers[answerIndex],
          ...updates,
        } as FormChecklistAnswer;

        return [
          ...prev.slice(0, itemIndex),
          { ...item, answers: updatedAnswers },
          ...prev.slice(itemIndex + 1),
        ];
      });
    },
    [setEditing]
  );

  const handleDeleteAnswer = useCallback(
    (itemId: string, answerId: string) => {
      setEditing(true, formName);
      setItems((prev) => {
        const itemIndex = findFormEntityIndex(prev, itemId);
        if (itemIndex === -1) return prev;

        const item = prev[itemIndex];
        const previousAnswers = item.answers || [];
        const filteredAnswers = previousAnswers.filter(
          (a) => !matchesFormEntityId(a, answerId)
        );
        const errorsByAnswerId = checklistAnswerErrorsByAnswerId(
          previousAnswers,
          item.errors.answers
        );
        const remappedAnswerErrors = filteredAnswers.map(
          (answer) =>
            errorsByAnswerId.get(answer._id || answer.uniqueKeyId) ?? {}
        );

        return [
          ...prev.slice(0, itemIndex),
          {
            ...item,
            answers: filteredAnswers,
            errors: {
              ...item.errors,
              answers: remappedAnswerErrors,
            },
          },
          ...prev.slice(itemIndex + 1),
        ];
      });
    },
    [setEditing]
  );

  const validateForm = useCallback((): boolean => {
    const summaryError = summaryValidation(summary);
    const processError = processValidation(process);
    const descriptionError = descriptionValidation(generalNorm);

    const sectionErrors = sections.map((section) => ({
      title: titleValidation(section.title),
      description: descriptionValidation(section.description),
    }));

    const itemErrors = items.map((item) => ({
      summary: summaryValidation(item.summary),
      description: descriptionValidation(item.description),
      answers: validateFormChecklistAnswers(item.answers || []),
    }));

    const checklistFocus = findFirstChecklistValidationFocus(items, itemErrors);

    setErrors((prev) =>
      chooseOptimalErrors(prev, {
        summary: summaryError,
        process: processError,
        description: descriptionError,
      })
    );

    setSections(chooseOptimalSectionErrors(sections, sectionErrors));
    setItems(chooseOptimalChecklistErrors(items, itemErrors));
    setChecklistValidationFocus(checklistFocus);

    scrollToFirstError(
      summaryError,
      processError,
      descriptionError,
      items,
      itemErrors,
      checklistFocus
    );

    const hasSectionErrors = sectionErrors.some((s) => s.title || s.description);
    const hasItemErrors = itemErrors.some(
      (itemError) =>
        Boolean(itemError.summary || itemError.description) ||
        hasAnswersTabErrors(itemError.answers)
    );

    const isValid =
      !summaryError &&
      !processError &&
      !descriptionError &&
      !hasSectionErrors &&
      !hasItemErrors;

    if (isValid) {
      setChecklistValidationFocus(null);
    }

    return isValid;
  }, [sections, items, summary, generalNorm, process]);

  const onSubmit =
    useCallback((): Promise<null | TemplateWizardSubmitResult> => {
      if (!validateForm()) {
        return Promise.resolve(null);
      }
      const template: TemplateValidItem = {
        process: process!,
        summary: summary,
        generalNorm: generalNorm,
        generalNormConfig,
      };

      return submitHandler(
        template,
        GU,
        items,
        sections,
        originalDefaultValues._id,
        originalDefaultValues
      );
    }, [
      GU,
      process,
      summary,
      generalNorm,
      generalNormConfig,
      items,
      sections,
      originalDefaultValues,
      validateForm,
      submitHandler,
    ]);

  const handleSaveDraft = useCallback(() => {
    return createDialog({
      title: <FormattedMessage id={DialogTranslationKeys.saveAsDraftQuestionDialogTitle} />,
      description: (
        <span>
          <FormattedMessage id={DialogTranslationKeys.saveAsDraftQuestionDialogHelperText} />
        </span>
      ),
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.cancel),
        translationKeyToString(CommonTranslationKeys.saveAsDraft),
      ],
    }).then(() => {
      setSaveDraftInProgress(true);
      return onSubmit()
        .then(
          (newestTemplate: null | TemplateWizardSubmitResult) => {
            if (!newestTemplate) {
              setSaveDraftInProgress(false);
              return;
            }

            setSaveDraftInProgress(false);
            setEditing(false, formName);
            
            openInspectionTemplate(newestTemplate._id);
            
            redirect(
              `/project/${projectId}/inspectionTemplate/${newestTemplate._id}`
            );
          }
        )
        .catch((error) => {
          logError('Saving template as draft failed', error);

          setSaveDraftInProgress(false);
          showErrorToastAndLog('Error while saving template as draft',
            error
          );
        });
    });
  }, [
    createDialog,
    onSubmit,
    setEditing,
    openInspectionTemplate,
    redirect,
    projectId,
  ]);

  const handlePublish = useCallback(() => {
    if (!canPublishInspectionTemplate(items)) {
      showErrorToastWithMessage(
        InspectionTemplateTranslationKeys.publishRequiresChecklistItem
      );
      return;
    }

    return createDialog({
      title: <FormattedMessage id={DialogTranslationKeys.publishQuestionDialogTitle} />,
      description: (
        <span>
          <FormattedMessage id={DialogTranslationKeys.publishQuestionDialogHelperText} />
        </span>
      ),
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.cancel),
        translationKeyToString(CommonTranslationKeys.publish),
      ],
    }).then(() => {
      setPublishing(true);
      return onSubmit()
        .then((templateResponse) => {
          if (!templateResponse) {
            setPublishing(false);
            return;
          }
          return publishHandler(templateResponse._id);
        })
        .then((newestTemplate: TemplateWizardSubmitResult | void) => {
            if (!newestTemplate) {
              setPublishing(false);
              return;
            }

            setPublishing(false);
            setEditing(false, formName);
            
            openInspectionTemplate(newestTemplate._id);
            
            redirect(
              `/project/${projectId}/inspectionTemplate/${newestTemplate._id}`
            );
          })
        .catch((error) => {
          logError('Publishing template failed', error);

          setPublishing(false);
          showErrorToastAndLog('Error while publishing template',
            error
          );
        });
    });
  }, [
    createDialog,
    items,
    onSubmit,
    publishHandler,
    setEditing,
    openInspectionTemplate,
    redirect,
    projectId,
  ]);

  const handleCancel = useCallback(() => {
    createDialog({
      title: <FormattedMessage id={DialogTranslationKeys.discardQuestionDialogTitle} />,
      description: (
        <span>
          <FormattedMessage id={DialogTranslationKeys.discardQuestionDialogHelperText} />
        </span>
      ),
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.cancel),
        translationKeyToString(CommonTranslationKeys.discard),
      ],
    }).then(() => {
      redirect(`/project/${projectId}/inspectionTemplate`);
    });
  }, [createDialog, redirect, projectId]);

  useEffect(() => {
    if (saveDraft) {
      handleSaveDraft();
    }
    setSaveDraft(false);
  }, [saveDraft, handleSaveDraft, setSaveDraft]);

  useEffect(() => {
    if (saveAndPublish) {
      handlePublish();
    }
    setSaveAndPublish(false);
  }, [saveAndPublish, handlePublish, setSaveAndPublish]);

  const onSaveDraftClick = useCallback(() => {
    setSaveDraft(true);
  }, [setSaveDraft]);

  const onPublishClick = useCallback(() => {
    setSaveAndPublish(true);
  }, [setSaveAndPublish]);

  return (
    <MemoPresentational
      pageTitleId={pageTitleId}
      defaultValues={refDefaultValues.current}
      handleProcessChange={handleProcessChange}
      sections={sections}
      items={items}
      errors={errors}
      process={process}
      processes={processes}
      isSaveDraftInProgress={isSaveDraftInProgress}
      isPublishing={isPublishing}
      handleSaveDraft={onSaveDraftClick}
      handlePublish={onPublishClick}
      handleCancel={handleCancel}
      handleTemplateSummaryChange={handleTemplateSummaryChange}
      handleGeneralNormDescriptionChange={
        handleGeneralNormDescriptionChange
      }
      generalNormDescription={generalNorm}
      generalNormConfig={generalNormConfig}
      handleGeneralNormConfigChange={handleGeneralNormConfigChange}
      graphicUploader={GU}
      handleSectionTitleChange={handleSectionTitleChange}
      handleSectionDescriptionChange={handleSectionDescriptionChange}
      handleSectionGraphicUploaderInit={
        handleSectionGraphicUploaderInit
      }
      handleAddSection={handleAddSection}
      handleDeleteSection={handleDeleteSection}
      handleItemSummaryChange={handleItemSummaryChange}
      handleItemDescriptionChange={handleItemDescriptionChange}
      handleItemGraphicUploaderInit={handleItemGraphicUploaderInit}
      handleAddItem={handleAddItem}
      handleDeleteItem={handleDeleteItem}
      handleReorderSection={handleReorderSection}
      handleReorderItem={handleReorderItem}
      handleAddAnswer={handleAddAnswer}
      handleUpdateAnswer={handleUpdateAnswer}
      handleDeleteAnswer={handleDeleteAnswer}
      checklistValidationFocus={checklistValidationFocus}
    />
  );
}

export const MemoTemplateWizardForm = React.memo(TemplateWizardForm);
